import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { LanguageWithWildcard } from "@/shared/services/graphql/generated/consumer-graph-types";

export const LOCAL_STORAGE_KEY = "qit-languages";
const DEFAULT_LOCALE = "en";
const DEFAULT_AVAILABLE_LOCALES = ["de", "en", "es", "fr", "it", "ja", "zh"];

export interface LanguageSettings {
    uiLocale: string;
    databaseLocale: string;
    fallbackLocales: string[];
    availableLocales: string[];
}

const defaultLanguageSettings: LanguageSettings = {
    uiLocale: DEFAULT_LOCALE,
    databaseLocale: DEFAULT_LOCALE,
    fallbackLocales: [DEFAULT_LOCALE],
    availableLocales: DEFAULT_AVAILABLE_LOCALES,
};

// --- Private localStorage helpers ---
function persistToLocalStorage(settings: LanguageSettings) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
}

function readFromLocalStorage(): LanguageSettings | null {
    const json = localStorage.getItem(LOCAL_STORAGE_KEY);
    return json ? JSON.parse(json) : null;
}

export const useLanguageStore = defineStore("lang", () => {
    const languageSettings = ref<LanguageSettings>(defaultLanguageSettings);

    // --- Computed getters ---
    const uiLocale = computed(() => languageSettings.value.uiLocale);
    const databaseLocale = computed(() => languageSettings.value.databaseLocale);
    const fallbackLocales = computed(() => languageSettings.value.fallbackLocales);
    const availableLocales = computed(() => languageSettings.value.availableLocales);

    const dataQueryLanguages = computed<LanguageWithWildcard[]>(() => {
        const languages = [languageSettings.value.databaseLocale, ...languageSettings.value.fallbackLocales];
        return languages.flatMap((elem) => {
            const value = Object.values(LanguageWithWildcard).find((v) => v === elem);
            return value ? [value] : [];
        });
    });

    // --- Actions ---
    function setLanguages(settings: LanguageSettings) {
        languageSettings.value = settings;
        persistToLocalStorage(settings);
    }

    function setUiLanguage(locale: string) {
        setLanguages({
            uiLocale: locale,
            databaseLocale: locale,
            fallbackLocales: fallbackLocales.value,
            availableLocales: availableLocales.value,
        });
    }

    function loadFromLocalStorage() {
        const saved = readFromLocalStorage();
        if (saved) {
            languageSettings.value = saved;
        }
    }

    return {
        languageSettings,
        uiLocale,
        databaseLocale,
        fallbackLocales,
        availableLocales,
        dataQueryLanguages,
        setLanguages,
        setUiLanguage,
        loadFromLocalStorage,
    };
});
